import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/env.js';

// Custom Audio Player Component
const AudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Workaround to get duration for WebM files that don't have it in metadata
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const getDuration = () => {
      // If duration is already valid, use it
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
        setIsLoaded(true);
        return;
      }

      // WebM workaround: seek to end to force browser to calculate duration
      const handleSeeked = () => {
        if (isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
          audio.currentTime = 0; // Reset to beginning
        }
        setIsLoaded(true);
        audio.removeEventListener('seeked', handleSeeked);
      };

      audio.addEventListener('seeked', handleSeeked);
      // Seek to a very large number - browser will clamp to actual duration
      audio.currentTime = Number.MAX_SAFE_INTEGER;
    };

    // Try to get duration when metadata loads
    const handleMetadata = () => {
      getDuration();
    };

    audio.addEventListener('loadedmetadata', handleMetadata);

    // If already loaded, try now
    if (audio.readyState >= 1) {
      getDuration();
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleMetadata);
    };
  }, [src]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      // Also update duration if it becomes available during playback
      if (duration === 0 && isFinite(audioRef.current.duration) && audioRef.current.duration > 0) {
        setDuration(audioRef.current.duration);
      }
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      padding: '12px 16px',
    }}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={(e) => console.error('Audio error:', e)}
      />

      {/* Play/Pause Button */}
      <button
        onClick={togglePlayPause}
        disabled={!isLoaded}
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          border: '2px solid #059669',
          background: 'white',
          cursor: isLoaded ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.2s ease',
          boxShadow: isLoaded ? '0 2px 8px rgba(5, 150, 105, 0.2)' : 'none',
          opacity: isLoaded ? 1 : 0.5,
        }}
      >
        {isPlaying ? (
          // Pause Icon - two vertical bars using CSS
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ width: '5px', height: '18px', background: '#059669', borderRadius: '2px' }} />
            <div style={{ width: '5px', height: '18px', background: '#059669', borderRadius: '2px' }} />
          </div>
        ) : (
          // Play Icon - CSS triangle
          <div style={{
            width: 0,
            height: 0,
            borderTop: '10px solid transparent',
            borderBottom: '10px solid transparent',
            borderLeft: '16px solid #059669',
            marginLeft: '4px'
          }} />
        )}
      </button>

      {/* Progress Section */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Time Display */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: 'rgba(255, 255, 255, 0.7)',
          fontFamily: 'monospace',
        }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Progress Bar */}
        <div
          onClick={handleSeek}
          style={{
            height: '6px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
            cursor: isLoaded ? 'pointer' : 'default',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Progress Fill */}
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            borderRadius: '3px',
            transition: 'width 0.1s linear',
          }} />

          {/* Progress Knob */}
          {isLoaded && (
            <div style={{
              position: 'absolute',
              left: `${progress}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '12px',
              height: '12px',
              background: 'white',
              borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              transition: 'left 0.1s linear',
            }} />
          )}
        </div>
      </div>

      {/* Waveform Icon */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        opacity: isPlaying ? 1 : 0.5,
        transition: 'opacity 0.2s',
      }}>
        {[12, 20, 16, 24, 14, 18, 10].map((h, i) => (
          <div
            key={i}
            style={{
              width: '3px',
              height: `${h}px`,
              background: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '2px',
              animation: isPlaying ? `wave 0.5s ease-in-out ${i * 0.1}s infinite alternate` : 'none',
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes wave {
          0% { transform: scaleY(0.5); }
          100% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
};

const ReportViewer = ({ consultationId, onClose, onEdit }) => {
  const { user, authFetch } = useAuth();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  useEffect(() => {
    fetchConsultation();
  }, [consultationId]);

  const fetchConsultation = async () => {
    try {
      setLoading(true);
      const response = await authFetch(
        getApiUrl(`/api/consultations/get?consultation_id=${consultationId}`)
      );
      const data = await response.json();

      if (data.success) {
        console.log('Consultation loaded:', data.consultation);
        setConsultation(data.consultation);

        // Fetch audio URL if available
        if (data.consultation.has_audio) {
          fetchAudioUrl();
        }

        setError(null);
      } else {
        console.error('Failed to load consultation:', data.message);
        setError(data.message || 'Failed to fetch consultation');
      }
    } catch (err) {
      console.error('Error fetching consultation:', err);
      setError('Failed to load consultation');
    } finally {
      setLoading(false);
    }
  };

  const fetchAudioUrl = async () => {
    try {
      const response = await authFetch(
        getApiUrl(`/api/consultations/audio-url?consultation_id=${consultationId}`)
      );
      const data = await response.json();

      if (data.success && data.url) {
        console.log('Audio URL received:', data.url);
        setAudioUrl(data.url);
        setAudioLoaded(false); // Reset loaded state for new URL
      }
    } catch (err) {
      console.error('Error fetching audio URL:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ReportSection = ({ title, content, icon }) => {
    if (!content || content.trim() === '') return null;

    return (
      <div style={{
        marginBottom: '24px',
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px',
          paddingBottom: '12px',
          borderBottom: '2px solid #059669'
        }}>
          <i className={`fas ${icon}`} style={{
            fontSize: '18px',
            color: '#059669'
          }}></i>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#059669',
            margin: 0
          }}>
            {title}
          </h3>
        </div>
        <div style={{
          fontSize: '15px',
          color: '#374151',
          lineHeight: '1.8',
          whiteSpace: 'pre-wrap'
        }}>
          {content}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <i className="fas fa-spinner fa-spin" style={{
            fontSize: '48px',
            color: '#059669',
            marginBottom: '16px'
          }}></i>
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            margin: 0
          }}>
            Loading report...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <i className="fas fa-exclamation-circle" style={{
            fontSize: '48px',
            color: '#dc2626',
            marginBottom: '16px'
          }}></i>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            Error
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 24px 0'
          }}>
            {error}
          </p>
          <button
            onClick={onClose}
            style={{
              background: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!consultation) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 2000,
      overflow: 'auto',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: 'linear-gradient(135deg, #f0fdfa 0%, #e6fffa 50%, #ccfbf1 100%)',
        borderRadius: '20px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #059669, #06b6d4)',
          borderRadius: '20px 20px 0 0',
          padding: '32px',
          color: 'white'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                margin: '0 0 8px 0',
                fontFamily: "'Poppins', sans-serif"
              }}>
                Consultation Report
              </h2>
              <p style={{
                fontSize: '14px',
                opacity: 0.9,
                margin: 0
              }}>
                {formatDate(consultation.created_at)}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: 'white',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Patient Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginTop: '24px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              padding: '16px'
            }}>
              <div style={{
                fontSize: '12px',
                opacity: 0.8,
                marginBottom: '4px'
              }}>
                Patient
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600'
              }}>
                {consultation.patient_name}
              </div>
              {consultation.patient_dob && (
                <div style={{
                  fontSize: '13px',
                  opacity: 0.8,
                  marginTop: '4px'
                }}>
                  Born on {consultation.patient_dob}
                </div>
              )}
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              padding: '16px'
            }}>
              <div style={{
                fontSize: '12px',
                opacity: 0.8,
                marginBottom: '4px'
              }}>
                Physician
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600'
              }}>
                Dr. {consultation.doctor_name || user.name}
              </div>
              {consultation.doctor_specialty && (
                <div style={{
                  fontSize: '13px',
                  opacity: 0.8,
                  marginTop: '4px'
                }}>
                  {consultation.doctor_specialty}
                </div>
              )}
            </div>

            {consultation.has_audio && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '12px',
                padding: '16px',
                gridColumn: '1 / -1'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  opacity: 0.8,
                  marginBottom: '12px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                  Consultation audio recording
                </div>
                {audioUrl ? (
                  <AudioPlayer src={audioUrl} />
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                  }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid rgba(255,255,255,0.5)',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)' }}>
                        Loading audio...
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                        Please wait
                      </div>
                    </div>
                    <style>{`
                      @keyframes spin {
                        to { transform: rotate(360deg); }
                      }
                    `}</style>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Report Content */}
        <div style={{
          padding: '32px'
        }}>
          <ReportSection
            title="Chief Complaint"
            content={consultation.motif_consultation}
            icon="fa-clipboard-list"
          />

          <ReportSection
            title="History of Present Illness"
            content={consultation.histoire_maladie}
            icon="fa-book-medical"
          />

          <ReportSection
            title="Past Medical History"
            content={consultation.antecedents}
            icon="fa-history"
          />

          <ReportSection
            title="Vital Signs"
            content={consultation.constantes}
            icon="fa-heartbeat"
          />

          <ReportSection
            title="Clinical Examination"
            content={consultation.examen_clinique}
            icon="fa-stethoscope"
          />

          <ReportSection
            title="Additional Tests"
            content={consultation.examens_complementaires}
            icon="fa-vial"
          />

          <ReportSection
            title="Prescription"
            content={consultation.ordonnance}
            icon="fa-prescription"
          />

          <ReportSection
            title="Conclusion and Treatment"
            content={consultation.conclusion_traitement}
            icon="fa-notes-medical"
          />
        </div>

        {/* Footer Actions */}
        <div style={{
          padding: '24px 32px',
          borderTop: '1px solid rgba(5, 150, 105, 0.1)',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={() => {
              if (onEdit && consultation) {
                // Convert consultation data to EditReport format with extractedFacts structure
                const reportData = {
                  extractedFacts: {
                    consultation: { value: '', confidence: 1 },
                    patient: { value: '', confidence: 1 },
                    medecin: { value: '', confidence: 1 },
                    motif_consultation: { value: consultation.motif_consultation || '', confidence: 1 },
                    histoire_maladie: { value: consultation.histoire_maladie || '', confidence: 1 },
                    antecedents: { value: consultation.antecedents || '', confidence: 1 },
                    constantes: { value: consultation.constantes || '', confidence: 1 },
                    examen_clinique: { value: consultation.examen_clinique || '', confidence: 1 },
                    examens_complementaires: { value: consultation.examens_complementaires || '', confidence: 1 },
                    ordonnance: { value: consultation.ordonnance || '', confidence: 1 },
                    conclusion_traitement: { value: consultation.conclusion_traitement || '', confidence: 1 }
                  },
                  consultationId: consultation.id
                };

                const patientInfo = {
                  prenom: consultation.patient_name?.split(' ')[0] || '',
                  nom: consultation.patient_name?.split(' ').slice(1).join(' ') || '',
                  dateNaissance: consultation.patient_dob || '',
                  sexe: consultation.patient_sex || '',
                  adresse: ''
                };

                onEdit(reportData, patientInfo, consultation.id);
              }
            }}
            style={{
              background: 'white',
              border: '2px solid #059669',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#059669',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="fas fa-edit"></i>
            Edit
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #059669, #06b6d4)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="fas fa-check"></i>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;
